import { useContext, useEffect, useMemo, useState } from 'react';
import { ClickAwayListener } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSlateSelection, useSlateStatic } from 'slate-react';

import { EditIcon } from 'src/assets/icons/EditIcon';
import { UnlinkIcon } from 'src/assets/icons/Unlink';
import { CUSTOM_ICON_BUTTON_SIZE, CustomIconButton } from 'src/components/CustomIconButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { getEditorSelectedLinkNode } from 'src/lib/slate';
import { addProtocolIfMissing } from 'src/lib/url';

import { selectFocusedLeaf, useSelectionDomRange, useSetupLinkPopupFloating } from '../../lib';
import { isLinkActive, unwrapLink } from '../../utils';
import { CustomSlateContext } from '../CustomSlate';

import { useStyles } from './useStyles';

const propTypes = {
  onClose: PropTypes.func.isRequired,
};

export const LinkViewPopover = ({ onClose }) => {
  const styles = useStyles();
  const editor = useSlateStatic();
  const selection = useSlateSelection();
  const { openEditLinkPopup } = useContext(CustomSlateContext);

  const [floatingElement, setFloatingElement] = useState(null);
  const selectionDomRange = useSelectionDomRange();

  useEffect(() => {
    // Check that we actually have a "link" node in the current selection
    const linkNode = getEditorSelectedLinkNode(editor);

    if (!selectionDomRange || !linkNode) {
      onClose();
    }
  }, [editor, onClose, selectionDomRange]);

  const { floatingStyles } = useSetupLinkPopupFloating(floatingElement, selectionDomRange);

  const url = useMemo(() => {
    if (!selection) {
      return '';
    }

    // Find the current URL value
    const linkNode = getEditorSelectedLinkNode(editor);
    return linkNode?.url || '';
  }, [editor, selection]);

  const handleEditClick = () => {
    selectFocusedLeaf(editor);
    openEditLinkPopup();
  };

  const handleClearClick = () => {
    if (!isLinkActive(editor)) {
      return;
    }

    unwrapLink(editor);
    onClose();
  };

  if (!selectionDomRange) {
    return null;
  }

  return (
    <ClickAwayListener
      onClickAway={onClose}
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
    >
      <div
        className={styles.root}
        ref={setFloatingElement}
        style={floatingStyles}
        data-floating-link={true}
      >
        <CustomTypography
          className={styles.title}
          variant={TYPOGRAPHY_VARIANT.regular12}
        >
          Visit URL:
        </CustomTypography>

        <a
          className={styles.link}
          href={addProtocolIfMissing(url)}
          rel="noreferrer"
          referrerPolicy="no-referrer"
          target="_blank"
        >
          {url}
        </a>

        <CustomIconButton
          className={styles.button}
          onClick={handleEditClick}
          size={CUSTOM_ICON_BUTTON_SIZE.small}
        >
          <EditIcon className={styles.icon} />
        </CustomIconButton>
        <CustomIconButton
          className={styles.button}
          onClick={handleClearClick}
          size={CUSTOM_ICON_BUTTON_SIZE.small}
        >
          <UnlinkIcon className={styles.icon} />
        </CustomIconButton>
      </div>
    </ClickAwayListener>
  );
};

LinkViewPopover.displayName = 'LinkViewPopover';
LinkViewPopover.propTypes = propTypes;
